export { setGraphqlClientCommonOptions } from '@sceneio/graphql-queries/src'
import type {
  DocumentName,
  GetVarsByDocumentName,
  GqlResultByDocumentName,
  GqlResultDataByDocumentName,
  GraphqlSendGraphqlOptions,
} from '@sceneio/graphql-queries/dist/client/types'

import { graphqlClientFactory } from '@sceneio/graphql-queries/src'
import { stringify } from '@sceneio/tools/lib/stringify'
import { useCallback, useEffect, useState } from 'react'
import { captureException, addBreadcrumb } from '@sentry/react'
import { useLocation } from 'react-router-dom'
import {
  selectMyWorkspaceId,
  useUserSelector,
} from '@sceneio/content-store/lib/features/user/userSlice'
import { useWorkspaceSelector } from '@sceneio/content-store/lib/features/workspace/workspaceSlice'
import { selectSelectedWorkspaceId } from '@sceneio/content-store/lib/features/workspace/workspaceSliceSelectors'

interface HookProps<T extends DocumentName, V> {
  query: T
  variables?: V
  runOnInit?: boolean
  invalidateCond?: Array<string | number>
  skipCheckToken?: boolean
  onError?: (err: any) => void
  onSuccess?: (res: GqlResultDataByDocumentName<T>) => void
  authContext?: GraphqlSendGraphqlOptions['authContext']
}

export function useInternalGraphql<
  T extends DocumentName,
  V extends GetVarsByDocumentName<T>,
  Response extends GqlResultByDocumentName<T>,
  ResponseData extends GqlResultDataByDocumentName<T>,
>(hookArgs: HookProps<T, V>) {
  const {
    query,
    variables,
    invalidateCond = [],
    runOnInit = true,
    onError,
    onSuccess,
    authContext: hookAuthContext,
  } = hookArgs || {}
  // TODO replace with url once routes has been refactored
  const myWorkspaceId = useUserSelector(selectMyWorkspaceId)
  const selectedWorkspaceId = useWorkspaceSelector(selectSelectedWorkspaceId)
  const location = useLocation()
  const urlProjectId = location.pathname.match(/project\/([a-z0-9]+)\//)?.[1]

  if (!query) {
    throw Error('Property `query` not defined')
  }

  const [state, setState] = useState({
    data: null as ResponseData,
    error: false,
    silentError: false,
    loading: runOnInit,
    silentLoading: runOnInit,
  })

  const changeState = useCallback(
    (args: Partial<typeof state>) => {
      setState((prevState) => ({
        ...prevState,
        ...args,
      }))
    },
    [stringify(state)],
  )

  const sendRequest = (options?: {
    variables?: V | null
    onlySilentLoading?: boolean
    onlySilentError?: boolean
    authContext?: GraphqlSendGraphqlOptions['authContext']
  }) => {
    const {
      variables: requestVars,
      onlySilentLoading = false,
      onlySilentError = false,
      authContext: requestAuthContext = {},
    } = options || {}

    changeState({
      loading: !onlySilentLoading,
      silentLoading: true,
      error: false,
      silentError: false,
    })

    // Log request to Sentry
    addBreadcrumb({
      category: 'graphql',
      message: `GraphQL Request ${query}`,
      data: {
        query,
        variables: requestVars || variables,
      },
    })

    // Auth Chronology: request > hook > url/redux (global)
    return graphqlClientFactory()
      .graphql(query, requestVars || variables, {
        authContext: {
          workspaceId:
            requestAuthContext?.workspaceId ||
            hookAuthContext?.workspaceId ||
            selectedWorkspaceId ||
            myWorkspaceId,
          projectId:
            requestAuthContext?.projectId ||
            hookAuthContext?.projectId ||
            urlProjectId,
        },
      })
      .then((res) => {
        const { data, error } = res as unknown as Response

        const gqlErrors = (error?.graphQLErrors || []).map(
          ({ message = '', extensions: { code = '' } }) => ({ message, code }),
        )

        // Log response to Sentry
        addBreadcrumb({
          category: 'graphql',
          message: `GraphQL Response ${query}`,
          data: {
            query,
            variables: requestVars || variables,
            data,
            errors: gqlErrors,
          },
        })

        // Log server errors
        if (gqlErrors.length) {
          gqlErrors.forEach(({ message }) => {
            console.log(
              `[useInternalGraphql] Server error - ${query}: ${message}`,
            )
          })
        }

        changeState({
          data: data as ResponseData,
          loading: false,
          silentLoading: false,
          error: false,
          silentError: false,
        })

        onSuccess && onSuccess(data as ResponseData)

        return {
          data: data as ResponseData,
          errors: gqlErrors,
        }
      })
      .catch((err) => {
        console.warn('[useInternalGraphql]', err.message)

        // Log error to Sentry
        captureException(err, {
          extra: {
            query,
            variables: requestVars || variables,
          },
        })

        changeState({
          error: !onlySilentError,
          silentError: true,
          loading: false,
          silentLoading: false,
        })

        onError && onError(err)

        return {
          data: null as ResponseData,
          errors: [{ message: err.message, code: '' }],
        }
      })
  }

  useEffect(() => {
    if (!runOnInit) return
    sendRequest()
  }, [query, stringify(variables), ...invalidateCond])

  return {
    loading: state.loading,
    silentLoading: state.silentLoading,
    error: state.error,
    silentError: state.silentError,
    data: state.data,
    sendRequest,
  }
}
